import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Layout, Table, List, Skeleton, message, Image } from 'antd';
import { Button } from 'primereact/button';
import { Carousel } from 'primereact/carousel';
import { Dropdown } from 'primereact/dropdown';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';
import './Assets.css';

import LazyPhoto from './LazyPhoto';

import {
  useGetAssetsQuery,
  useGetProjectDrawingsQuery,
  usePatchSurveyLocationMutation,
  usePatchSurveyPhotoMutation,
} from '../../features/api/apiSlice';

import { Map, Marker, MapStyle, config as maptilerConfig } from '@maptiler/sdk';
import AssignLayer from './AssignLayer';
import AddAssetForm from './AddAsset';
import ExportDwgAssetsModal from './ExportDwgAssets';
import AddNote from './AddNote';
import EditLocation from './EditLocation';
import AssetsTableRow from './AssetsTableRow';
import EditGeneral from './EditGeneral';
maptilerConfig.apiKey = process.env.REACT_APP_MAP_KEY;

export default function Assets({
  assetsSelected,
  setAssetsSelected,
  updateCurrentAsset,
  currentAsset,
  isAssignLayerOpen,
  setAssignLayerOpen,
}) {
  const { projectId } = useParams();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();

  const [addNoteVisible, setAddNoteVisible] = useState(false);
  const [mapChoiceVisible, setMapChoiceVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [locationVisible, setLocationVisible] = useState(false);
  const [editGeneralVisible, setEditGeneralVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedNoteRows, setExpandedNoteRows] = useState([]);
  const [layers, setLayers] = useState([]);

  const { data: assetsData = [], isLoading, refetch } = useGetAssetsQuery(projectId);
  const [patchSurveyLocation] = usePatchSurveyLocationMutation();
  const [patchSurveyPhoto] = usePatchSurveyPhotoMutation();
  const { data } = useGetProjectDrawingsQuery(projectId);

  const markerRef = useRef(null);
  const mapRef = useRef(null);
  const refetchRef = useRef(refetch);

  // Update ref when refetch changes
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  // Only call updateCurrentAsset once on mount, using ref to avoid dependency
  const updateCurrentAssetRef = useRef(updateCurrentAsset);
  useEffect(() => {
    updateCurrentAssetRef.current = updateCurrentAsset;
  }, [updateCurrentAsset]);

  useEffect(() => {
    if (updateCurrentAssetRef.current && refetchRef.current) {
      updateCurrentAssetRef.current('refetchAll', refetchRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (data?.drawings) setLayers(data.drawings);
  }, [data]);

  // Memoize assets to prevent unnecessary re-renders
  const assets = useMemo(() => assetsData, [assetsData]);

  const handleAssetCreated = (newAsset) => {
    message.success(`Asset "${newAsset.name || 'Unnamed'}" created successfully!`);
    refetch();
  };

  const handleAssetCreationError = (error) => {
    console.error('Asset creation failed:', error);
    message.error('Failed to create asset. Please try again.');
  };

  const handleAssignLayer = useCallback(
    (asset) => {
      console.log('handleAssignLayer called', asset?.id);
      // Close all other modals and set state in a single batch
      setEditGeneralVisible(false);
      setLocationVisible(false);
      setAddNoteVisible(false);
      setSelectedAsset(asset);
      setAssignLayerOpen(true);
    },
    [setAssignLayerOpen]
  );

  const handleAddNote = useCallback(
    (asset) => {
      console.log('handleAddNote called', asset?.id);
      setEditGeneralVisible(false);
      setLocationVisible(false);
      setAssignLayerOpen(false);
      setSelectedAsset(asset);
      setAddNoteVisible(true);
    },
    [setAssignLayerOpen]
  );

  const handleEditLocation = useCallback(
    (asset) => {
      console.log('handleEditLocation called', asset?.id);
      setEditGeneralVisible(false);
      setAddNoteVisible(false);
      setAssignLayerOpen(false);
      setSelectedAsset(asset);
      setLocationVisible(true);
    },
    [setAssignLayerOpen]
  );

  const handleEditGeneral = useCallback(
    (asset) => {
      console.log('handleEditGeneral called', asset?.id);
      setLocationVisible(false);
      setAddNoteVisible(false);
      setAssignLayerOpen(false);
      setSelectedAsset(asset);
      setEditGeneralVisible(true);
    },
    [setAssignLayerOpen]
  );

  const getLayerById = useCallback(
    (layerId) => {
      const layer = layers.find((layer) => layer.id === layerId);
      return {
        color: layer?.color || null,
        name: layer?.name || null,
      };
    },
    [layers]
  );

  const showMapForAsset = ([lat, lng]) => {
    if (!mapRef.current) {
      mapRef.current = new Map({
        container: 'map',
        style: MapStyle.HYBRID,
        center: [lng, lat],
        zoom: 18,
      });

      markerRef.current = new Marker({ draggable: true })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);

      markerRef.current.on('dragend', () => {
        const lngLat = markerRef.current.getLngLat();
        document.getElementById('coordinates').innerHTML =
          `Lng: ${lngLat.lng} <br/> Lat: ${lngLat.lat}`;
      });
    } else {
      mapRef.current.setCenter([lng, lat]);
      markerRef.current.setLngLat([lng, lat]);
    }

    setMapChoiceVisible(true);
  };

  const handleConfirmLocationUpdate = async () => {
    try {
      const lngLat = markerRef.current.getLngLat();
      updateCurrentAsset('lngLat', { lng: lngLat.lng, lat: lngLat.lat });

      const locationBody = {
        projectId,
        surveyId: selectedAsset.id,
        latitude: lngLat.lat,
        longitude: lngLat.lng,
      };
      const photoBody = {
        projectId,
        surveyId: selectedAsset.id,
        uuid: currentAsset?.uuid?.file,
      };

      await patchSurveyPhoto(photoBody);
      await patchSurveyLocation(locationBody);
      await refetch();

      message.success('Location updated successfully!');
      setMapChoiceVisible(false);
      setDropdownVisible(false);
    } catch (err) {
      console.error('Update failed', err);
      message.error('Failed to update location. Please try again.');
    }
  };

  const renderAssetRow = useCallback((asset) => ({ key: asset.id, ...asset }), []);

  const tableDataSource = useMemo(() => {
    if (!assets || !Array.isArray(assets)) return [];
    return assets.map(renderAssetRow);
  }, [assets, renderAssetRow]);

  const selectedRowKeys = useMemo(() => assetsSelected.map((asset) => asset.id), [assetsSelected]);

  const assetsSelectedRef = useRef(assetsSelected);
  useEffect(() => {
    assetsSelectedRef.current = assetsSelected;
  }, [assetsSelected]);

  const handleRowSelectionChange = useCallback(
    (_selectedRowKeys, selectedRows) => {
      // Only update if the selection actually changed
      const current = assetsSelectedRef.current;
      if (
        !current ||
        current.length !== selectedRows.length ||
        current.some((asset, idx) => asset.id !== selectedRows[idx]?.id)
      ) {
        setAssetsSelected(selectedRows);
      }
    },
    [setAssetsSelected]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedRowKeys,
      onChange: handleRowSelectionChange,
    }),
    [selectedRowKeys, handleRowSelectionChange]
  );

  const carouselRef = useRef(null);
  const renderCarousel = useCallback((asset) => {
    const photos = asset.photos || [];
    return (
      <div style={{ width: '240px', height: '100px', overflow: 'hidden' }}>
        {photos.length === 0 ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
            }}
          >
            <p>No Photos</p>
          </div>
        ) : (
          <Carousel
            ref={carouselRef}
            value={photos}
            numScroll={1}
            numVisible={1}
            circular={true}
            autoplayInterval={3000}
            showIndicators={false}
            style={{ width: '100%', height: '100%' }}
            itemTemplate={(photo) => (
              <div style={{ width: '100%', height: '100px' }}>
                <LazyPhoto photo={photo} alt={photo.name || 'Asset photo'} objectFit="contain" />
              </div>
            )}
          />
        )}
      </div>
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        align: 'center',
        ellipsis: true,
        render: (name) =>
          name ? (
            <span title={name} style={{ fontWeight: 500 }}>
              {name}
            </span>
          ) : (
            <span style={{ color: '#999' }}>N/A</span>
          ),
      },
      {
        title: 'Photo',
        key: 'photo',
        align: 'center',
        width: '240px',
        render: (_text, asset) => renderCarousel(asset),
      },
      {
        title: 'Location',
        key: 'location',
        align: 'center',
        render: (survey) => (
          <Image
            style={{ cursor: 'pointer' }}
            onClick={() =>
              navigate(
                `/projects/${projectId}/map?&pointId=${survey.id}&lng=${survey.longitude}&lat=${survey.latitude}`
              )
            }
            preview={false}
            src={`
            https://api.maptiler.com/maps/nl-cartiqo-topo/static/${survey.longitude},${survey.latitude},14/100x100.png?key=${process.env.REACT_APP_MAP_KEY || 'Ha0wpPicSoh3DDh4limA'}&markers=${survey.longitude},${survey.latitude}
          `}
            fallback="/placeholder.svg"
          />
        ),
      },
      {
        title: 'Note',
        dataIndex: 'note',
        align: 'center',
        render: (note, record) => {
          if (!note) return <span style={{ color: '#999' }}>N/A</span>;

          const isExpanded = expandedNoteRows.includes(record.key);
          const lineCount = note.split(/\r\n|\r|\n/).length;
          const needsToggle = note.length > 140 || lineCount > 2;

          const toggle = () => {
            setExpandedNoteRows((prev) =>
              isExpanded ? prev.filter((k) => k !== record.key) : [...prev, record.key]
            );
          };

          return (
            <div style={{ maxWidth: 240, margin: '0 auto', textAlign: 'left' }}>
              <div
                style={{
                  maxHeight: isExpanded ? '14em' : '4.5em',
                  overflowY: isExpanded ? 'auto' : 'hidden',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {note}
              </div>
              {needsToggle && (
                <a
                  onClick={toggle}
                  style={{ display: 'block', marginTop: 4, cursor: 'pointer', fontSize: 12 }}
                >
                  {isExpanded ? 'less' : 'more'}
                </a>
              )}
            </div>
          );
        },
      },
      {
        title: 'Depth',
        dataIndex: 'depth',
        align: 'center',
        render: (depth) =>
          depth !== null && depth !== undefined ? (
            `${depth} m`
          ) : (
            <span style={{ color: '#999' }}>N/A</span>
          ),
      },
      {
        title: 'Installed',
        dataIndex: 'installed_at',
        align: 'center',
        render: (installed) =>
          installed ? (
            dayjs(installed).format('MMM DD YYYY')
          ) : (
            <span style={{ color: '#999' }}>N/A</span>
          ),
      },
      {
        title: 'Layer',
        dataIndex: 'drawing_id',
        align: 'center',
        ellipsis: true,
        render: (drawing_id) => {
          const { color, name } = getLayerById(drawing_id);
          if (!name) return <span style={{ color: '#999' }}>N/A</span>;
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
              }}
              title={name}
            >
              {color && (
                <span
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: color,
                    marginRight: 8,
                    display: 'inline-block',
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {name}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Recorded by',
        dataIndex: 'recorded_by',
        align: 'center',
        ellipsis: true,
        render: (name) =>
          name ? <span title={name}>{name}</span> : <span style={{ color: '#999' }}>Unknown</span>,
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        align: 'center',
        render: (_text, asset) => (
          <AssetsTableRow
            asset={asset}
            handleAssignLayer={handleAssignLayer}
            handleAddNote={handleAddNote}
            handleEditLocation={handleEditLocation}
            handleEditGeneral={handleEditGeneral}
          />
        ),
      },
    ],
    [
      expandedNoteRows,
      navigate,
      projectId,
      layers,
      handleAssignLayer,
      handleAddNote,
      handleEditLocation,
      handleEditGeneral,
      renderCarousel,
      getLayerById,
    ]
  );

  const renderMobileView = (asset) => {
    return (
      <List.Item
        key={asset.id}
        actions={[
          asset.longitude && asset.latitude ? (
            <a
              key="view-map"
              onClick={() =>
                navigate(
                  `/projects/${projectId}/map?assetId=${asset.id}&lng=${asset.longitude}&lat=${asset.latitude}`
                )
              }
            >
              View Map
            </a>
          ) : (
            <a key="add-location" onClick={() => showMapForAsset([52.377956, 4.89707])}>
              Add Location
            </a>
          ),
        ]}
      >
        <Skeleton loading={isLoading} active>
          <List.Item.Meta
            title={asset.name || 'Unnamed'}
            description={`Installed: ${dayjs(asset.installed_at).format(
              'MMM DD YYYY'
            )}\nRecorded by: ${asset.created_by}`}
          />
        </Skeleton>
        {renderCarousel(asset)}
      </List.Item>
    );
  };

  return (
    <Layout key={refreshKey}>
      {/* Map overlay */}
      {(mapChoiceVisible || dropdownVisible) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
          }}
        />
      )}

      {/* Map choice area */}
      {mapChoiceVisible && (
        <div>
          <div id="map" style={{ width: '100vw', height: '70vh', zIndex: 1001 }} />
          <pre
            id="coordinates"
            style={{
              color: '#fff',
              background: '#000a',
              padding: '10px',
              position: 'absolute',
              bottom: '10px',
              left: '10px',
              zIndex: 1002,
            }}
          />
          <div
            style={{
              position: 'fixed',
              bottom: '5vh',
              left: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              zIndex: 1003,
            }}
          >
            <Button icon="pi pi-times" onClick={() => setMapChoiceVisible(false)} />
            <Button icon="pi pi-check" onClick={() => setDropdownVisible(true)} />
          </div>
        </div>
      )}

      {/* Dropdown confirm */}
      {dropdownVisible && (
        <div
          style={{
            position: 'fixed',
            top: '40vh',
            left: 0,
            width: '100%',
            zIndex: 1004,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Dropdown
            value={selectedAsset}
            onChange={(e) => setSelectedAsset(e.value)}
            options={assets}
            optionLabel="name"
            placeholder="Assign to Asset"
            filter
            className="w-full md:w-14rem"
          />
          <div style={{ marginTop: 20, display: 'flex', gap: 20 }}>
            <Button
              icon="pi pi-times"
              severity="danger"
              onClick={() => setDropdownVisible(false)}
            />
            <Button icon="pi pi-check" onClick={handleConfirmLocationUpdate} />
          </div>
        </div>
      )}

      {/* Modals */}
      <AssignLayer
        modalOpen={isAssignLayerOpen}
        setModalOpen={(value) => {
          if (typeof setAssignLayerOpen === 'function') {
            setAssignLayerOpen(value);
          }
          if (!value) {
            setSelectedAsset(null);
          }
        }}
        selectedAsset={selectedAsset}
        setSelectedAsset={setSelectedAsset}
        assetsSelected={assetsSelected}
        setAssetsSelected={setAssetsSelected}
        refetch={() => {
          refetch();
          if (updateCurrentAsset) {
            updateCurrentAsset('refetchAll', refetch);
          }
        }}
        layers={layers}
      />

      {/* Add Note Modal */}
      <AddNote
        isModalOpen={addNoteVisible}
        setModalOpen={(value) => {
          if (typeof setAddNoteVisible === 'function') setAddNoteVisible(value);
          if (!value) setSelectedAsset(null);
        }}
        selectedAsset={selectedAsset}
        setSelectedAsset={(value) => setSelectedAsset(value)}
        refetch={() => {
          refetch();
          if (updateCurrentAsset) updateCurrentAsset('refetchAll', refetch);
        }}
      />

      {/* Edit Location Modal */}
      <EditLocation
        isModalOpen={locationVisible}
        setModalOpen={(value) => {
          if (typeof setLocationVisible === 'function') setLocationVisible(value);
          if (!value) setSelectedAsset(null);
        }}
        selectedAsset={selectedAsset}
        setSelectedAsset={(value) => setSelectedAsset(value)}
        refetch={() => {
          refetch();
          if (updateCurrentAsset) updateCurrentAsset('refetchAll', refetch);
        }}
      />

      {/* Edit General Modal */}
      <EditGeneral
        isModalOpen={editGeneralVisible}
        setModalOpen={(value) => {
          if (typeof setEditGeneralVisible === 'function') setEditGeneralVisible(value);
          if (!value) setSelectedAsset(null);
        }}
        selectedAsset={selectedAsset}
        setSelectedAsset={(value) => setSelectedAsset(value)}
        refetch={() => {
          refetch();
          if (updateCurrentAsset) updateCurrentAsset('refetchAll', refetch);
        }}
        onAssetCreated={handleAssetCreated}
        onAssetCreationError={handleAssetCreationError}
      />

      {/* Desktop table */}
      {!isMobile && (
        <Table
          columns={columns}
          dataSource={tableDataSource}
          loading={isLoading}
          rowSelection={rowSelection}
        />
      )}

      {/* Mobile list */}
      {isMobile && (
        <List
          itemLayout="vertical"
          size="large"
          loading={isLoading}
          style={{ margin: '75px 20px' }}
          dataSource={assets}
          renderItem={(asset) => renderMobileView(asset)}
        />
      )}
    </Layout>
  );
}

Assets.AddAsset = AddAssetForm;
Assets.ExportDwg = ExportDwgAssetsModal;
Assets.EditLocation = EditLocation;
Assets.EditGeneral = EditGeneral;
